import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { Heading2 } from '@hap/components'

import { ChannelScopingSection } from './-components/channel-scoping-section'
import { DirectionSelector, DirectionValue } from './-components/direction-selector'
import { ScorecardInfoForm } from './-components/scorecard-info-form'
import { WorkflowScopingSection } from './-components/workflow-scoping-section'

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/scorecards/$scorecardId/settings/',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const { scorecardId } = Route.useParams()
  const { data: scorecard } = useSuspenseQuery(
    trpc.scorecards.getById.queryOptions(scorecardId),
  )

  const { update } = ScorecardMutators.useUpdate()

  // Get workspace channel IDs for pre-filtering
  const { data: workspace } = useQuery(
    trpc.workspaces.getById.queryOptions(
      { workspaceId: scorecard.workspaceId! },
      { enabled: !!scorecard.workspaceId },
    ),
  )

  // Parse existing filters into UI state
  const scopingState = useMemo(
    () => parseFilters(scorecard.filters || []),
    [scorecard.filters],
  )

  // Helper to update filters on the server
  const updateFilters = useCallback(
    async (newState: Partial<typeof scopingState>) => {
      const merged = { ...scopingState, ...newState }
      const filters = buildFilters(merged)
      await update({ scorecardId, values: { filters } })
    },
    [scopingState, scorecardId, update],
  )

  // Direction change handler
  const handleDirectionChange = useCallback(
    (direction: DirectionValue) => {
      updateFilters({ direction })
    },
    [updateFilters],
  )

  // Channel handlers
  const handleAddChannel = useCallback(
    (channelId: string) => {
      if (!scopingState.channelIds.includes(channelId)) {
        updateFilters({ channelIds: [...scopingState.channelIds, channelId] })
      }
    },
    [scopingState.channelIds, updateFilters],
  )

  const handleRemoveChannel = useCallback(
    (channelId: string) => {
      updateFilters({
        channelIds: scopingState.channelIds.filter((id) => id !== channelId),
      })
    },
    [scopingState.channelIds, updateFilters],
  )

  // Workflow handlers
  const handleAddWorkflow = useCallback(
    (workflowId: string) => {
      if (!scopingState.workflowIds.includes(workflowId)) {
        updateFilters({ workflowIds: [...scopingState.workflowIds, workflowId] })
      }
    },
    [scopingState.workflowIds, updateFilters],
  )

  const handleRemoveWorkflow = useCallback(
    (workflowId: string) => {
      updateFilters({
        workflowIds: scopingState.workflowIds.filter((id) => id !== workflowId),
      })
    },
    [scopingState.workflowIds, updateFilters],
  )

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <Heading2>Details</Heading2>
        <ScorecardInfoForm
          scorecardId={scorecard.id}
          values={{
            name: scorecard.name,
            description: scorecard.description,
            workspaceId: scorecard.workspaceId,
          }}
        />
      </section>

      <section className="space-y-6">
        <div>
          <Heading2>Scoping</Heading2>
          <p className="text-muted-foreground mt-1">
            Define which interactions this scorecard applies to.
          </p>
        </div>

        <DirectionSelector
          value={scopingState.direction}
          onChange={handleDirectionChange}
        />

        <ChannelScopingSection
          channelIds={scopingState.channelIds}
          workspaceChannelIds={workspace?.channelIds || []}
          hasWorkspace={!!scorecard.workspaceId}
          onAdd={handleAddChannel}
          onRemove={handleRemoveChannel}
        />

        <WorkflowScopingSection
          workflowIds={scopingState.workflowIds}
          onAdd={handleAddWorkflow}
          onRemove={handleRemoveWorkflow}
        />
      </section>
    </div>
  )
}

function parseFilters(filters: AnalyticsQueryFilter[]) {
  let direction: DirectionValue = 'any'
  const channelIds: string[] = []
  const workflowIds: string[] = []

  for (const f of filters) {
    if (f.field === 'isOutbound' && 'value' in f && f.op === 'eq') {
      direction = f.value === true ? 'outbound' : 'inbound'
    }
    if (f.field === 'channelId' && 'values' in f && f.op === 'in') {
      channelIds.push(...(f.values as string[]))
    }
    if (f.field === 'workflowId' && 'values' in f && f.op === 'in') {
      workflowIds.push(...(f.values as string[]))
    }
  }

  return { direction, channelIds, workflowIds }
}

// Helper to build filters from UI state
function buildFilters(state: {
  direction: DirectionValue
  channelIds: string[]
  workflowIds: string[]
}): AnalyticsQueryFilter[] {
  const filters: AnalyticsQueryFilter[] = []

  if (state.direction !== 'any') {
    filters.push({
      field: 'isOutbound',
      op: 'eq',
      value: state.direction === 'outbound',
    })
  }

  if (state.channelIds.length > 0) {
    filters.push({
      field: 'channelId',
      op: 'in',
      values: state.channelIds,
    })
  }

  if (state.workflowIds.length > 0) {
    filters.push({
      field: 'workflowId',
      op: 'in',
      values: state.workflowIds,
    })
  }

  return filters
}
